import React, { useState, useEffect } from "react";
import { User, Award, Loader2, LogOut, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../api/axios";
import { toast } from "react-hot-toast";
import LogoutModal from "../../components/LogoutModal";
import DeleteAccountModal from "../../components/DeleteAccountModal";
import { useNavigate } from "react-router-dom";

const LANGUAGES = [
    { id: 'en', label: 'English' },
    { id: 'hi', label: 'Hinglish' }
];

export default function ProfileCore({ renderContent }) {
    const { logout } = useAuth();
    const { t, language, switchLanguage } = useLanguage();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/auth/me");
            setProfileData(data);
        } catch (err) {
            console.error(err);
            if (err.response?.status === 401 || err.response?.status === 404) {
                setProfileData(null);
            } else {
                toast.error("Could not load profile data");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    const handleFinalizeLogout = async () => {
        setShowLogoutModal(false);
        setIsLoggingOut(true);

        const toastId = toast.loading(t('gate_logout_prep'), {
            style: {
                borderRadius: '12px',
                background: '#4f46e5',
                color: '#fff',
            },
        });

        // Zen transition delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        toast.success(t('gate_logout_done'), { id: toastId });
        
        // Finalize logout
        logout();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
            </div>
        );
    }

    if (!profileData) {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-center">
                <Award className="w-16 h-16 text-gray-300 mb-4" />
                <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-200">Session Expired</h2>
                <p className="text-gray-500 mb-6">Please log in again to view your profile.</p>
                <button
                    onClick={logout}
                    className="px-6 py-2 bg-brand-blue text-white rounded-full hover:bg-blue-700 transition-colors"
                >
                    Log In
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <header className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
                    <User className="w-8 h-8 text-brand-blue" />
                    {t('prof_my_profile')}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mt-2">
                    {t('prof_manage_details')}
                </p>
            </header>

            <LogoutModal
                isOpen={showLogoutModal}
                onClose={() => setShowLogoutModal(false)}
                onConfirm={handleFinalizeLogout}
            />

            <DeleteAccountModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
            />

            {/* 🌐 Language Switcher within the Sanctuary */}
            <div className="mb-8 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                        <Award className="w-6 h-6 text-brand-blue" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-800 dark:text-gray-100">{t('nav_lang_toggle') || "Language / Bhasha"}</h3>
                        <p className="text-xs text-gray-500">{t('ob_hinglish_sub')}</p>
                    </div>
                </div>
                
                <div className="flex bg-gray-50 dark:bg-gray-800 p-1 rounded-2xl border border-gray-100 dark:border-gray-700">
                    {LANGUAGES.map((lang) => (
                        <button
                            key={lang.id}
                            onClick={() => switchLanguage(lang.id)}
                            className={`flex-1 py-3 px-6 rounded-xl text-sm font-bold transition-all duration-300 ${
                                language === lang.id
                                    ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            }`}
                        >
                            {lang.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* RENDER THE ROLE SPECIFIC WRAPPER CONTENT */}
            {renderContent(profileData, fetchProfile)}

            {/* Logout Section for Mobile/Convenience */}
            <div className="mt-12 pt-8 border-t border-gray-100 dark:border-gray-800">
                <button
                    onClick={() => setShowLogoutModal(true)}
                    disabled={isLoggingOut}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 rounded-2xl font-semibold hover:bg-red-100 dark:hover:bg-red-900/20 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                    {isLoggingOut ? (
                        <div className="flex items-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{t('ob_exhale')}</span>
                        </div>
                    ) : (
                        <>
                            <LogOut className="w-5 h-5" />
                            {t('nav_logout')}
                        </>
                    )}
                </button>

                <button
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 mt-3 bg-white dark:bg-gray-900 text-gray-400 dark:text-gray-500 rounded-2xl font-semibold border border-gray-100 dark:border-gray-800 hover:text-red-500 hover:border-red-100 transition-all text-xs uppercase tracking-widest"
                >
                    <Trash2 className="w-4 h-4" />
                    {t('set_close_account')}
                </button>

                <p className="text-center text-gray-400 text-xs mt-4">
                    {t('prof_journey_saved_msg')}
                </p>
            </div>
        </div>
    );
}
